import { AccountResourceService } from '@/openapi';
import { useState, useRef, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './PasswordResetInit.module.css';
import { useRemotePost } from '@/hooks/useRemotePost';
import Button from '@/components/UI/Button';
import InputField from '@/components/UI/InputField';
import Label from '@/components/UI/Label';
import { Link } from 'react-router-dom';
import Logo from '@/components/UI/Logo';
import Loader from '@/components/UI/Loader';
import { AuthContext } from '@/contexts/AuthContext';
import SuccessToast from '@/components/UI/SuccessToast';
import FadeInSection from '@/components/UI/FadeInSection';
import { useViewportHeight } from '@/hooks/useViewportHeight';

const PasswordResetInit = () => {
	useViewportHeight();

	const authCtx = useContext(AuthContext);

	useEffect(() => {
		authCtx.logout();
	}, []);

	const { t } = useTranslation();

	const [mail, setMail] = useState('');
	const [recoveryMailSent, setRecoveryMailSent] = useState(false);

	const mailInputRef = useRef<HTMLInputElement>(null);

	const resetPwd = useRemotePost<void, { requestBody: string }>({
		remoteMethod: async (payload) => {
			return AccountResourceService.requestPasswordReset(payload);
		}
	});

	useEffect(() => {
		if (typeof resetPwd.data !== 'undefined') {
			setRecoveryMailSent(true);
			if (mailInputRef.current) {
				mailInputRef.current.value = '';
			}
			setMail('');
		}
	}, [resetPwd.data]);

	useEffect(() => {
		if (typeof resetPwd.error !== 'undefined') {
			setRecoveryMailSent(true);
		}
	}, [resetPwd.error]);

	const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setRecoveryMailSent(false);

		await resetPwd.fetch({ requestBody: mail });
	};

	return (
		<div className={styles.main_container}>
			<form onSubmit={submitHandler} className={styles.form_container}>
				<Loader visible={resetPwd.loading} />

				<SuccessToast
					isVisible={recoveryMailSent}
					message={resetPwd.error ? t('pwdReset.errGeneric') : t('pwdReset.message')}
					onClose={() => {
						setRecoveryMailSent(false);
					}}
					type={resetPwd.error ? 'error' : 'success'}
				/>

				<FadeInSection className={styles.form_box}>
					<div className={styles.form_header}>
						<Logo className={styles.logo} onClick={() => {}} />
					</div>

					<div className={styles.form_content}>
						<div className={styles.box_message}>
							<Label className={styles.message_label} text={t('pwdReset.command')} />
						</div>

						<div className={styles.credentials_div}>
							<div className={styles.credential_couple}>
								<InputField
									type={'text'}
									title={t('pwdReset.mail')}
									required
									label={t('pwdReset.mail')}
									placeholder={t('pwdReset.mailPlaceholder')}
									ref={mailInputRef}
									onChange={(e) => {
										setMail(e.target.value);
									}}
								/>
							</div>
						</div>

						<div className={styles.box_btn}>
							<Button
								className={styles.btn_login}
								typeSubmit={'submit'}
								onClick={() => {}}
								name={t('pwdReset.reset')}
								color="black"
								background="white"
							/>
						</div>

						<Link to="/login" className={styles.navLink}>
							<p className={styles.getBack_label}>{t('pwdReset.back')}</p>
						</Link>
					</div>
				</FadeInSection>
			</form>
		</div>
	);
};

export default PasswordResetInit;
